import { useState, useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Menu, X } from 'lucide-react'
import { AnimatePresence, motion } from 'framer-motion'
import { cn } from '@/lib/utils'
import { LocalizedLink } from '@/components/LocalizedLink'
import { LanguageSelector } from '@/components/LanguageSelector'
import { localizedRoutes, type RouteKey } from '@/i18n/routes'

const navItems: { key: RouteKey; labelKey: string }[] = [
  { key: 'about', labelKey: 'nav.about' },
  { key: 'processes', labelKey: 'nav.processes' },
  { key: 'innovation', labelKey: 'nav.innovation' },
  { key: 'products', labelKey: 'nav.products' },
  { key: 'sectors', labelKey: 'nav.sectors' },
  { key: 'sustainability', labelKey: 'nav.sustainability' },
  { key: 'contacts', labelKey: 'nav.contacts' },
]

export function Header() {
  const { t } = useTranslation()
  const location = useLocation()
  const [isScrolled, setIsScrolled] = useState(false)
  const [isMobileOpen, setIsMobileOpen] = useState(false)

  // Detect active route from pathname
  const pathSegments = location.pathname.split('/').filter(Boolean)
  const currentSlug = pathSegments[1] || ''

  // Pages without a dark hero need the solid header immediately
  const pagesWithLightTop = ['contacts']
  const forceSolid = pagesWithLightTop.includes(currentSlug)

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setIsMobileOpen(false)
  }, [location.pathname])

  useEffect(() => {
    document.body.style.overflow = isMobileOpen ? 'hidden' : ''
    return () => { document.body.style.overflow = '' }
  }, [isMobileOpen])

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-fixed transition-all duration-normal',
        isScrolled || forceSolid
          ? 'bg-white/95 backdrop-blur-md shadow-md'
          : 'bg-transparent'
      )}
    >
      <nav aria-label={t('aria.mainNavigation', 'Main navigation')} className="mx-auto flex max-w-[1440px] items-center justify-between px-4 sm:px-6 lg:px-8 h-16 md:h-20">
        {/* Logo */}
        <LocalizedLink to="home" className="flex-shrink-0 no-underline">
          <img
            src="/images/PEL-logo.svg"
            alt="PEL Pintossi Emilio S.p.A."
            width={120}
            height={40}
            className={cn(
              'h-8 md:h-10 w-auto transition-all',
              !isScrolled && !forceSolid && 'brightness-0 invert'
            )}
          />
        </LocalizedLink>

        {/* Desktop Nav */}
        <div className="hidden lg:flex items-center gap-1">
          {navItems.map(({ key, labelKey }) => {
            const isActive = localizedRoutes[key] === currentSlug
            return (
              <LocalizedLink
                key={key}
                to={key}
                className={cn(
                  'group relative px-3 py-2 text-sm font-medium rounded-md no-underline transition-colors',
                  isScrolled || forceSolid
                    ? 'text-primary-800 hover:text-accent-600 hover:bg-neutral-50'
                    : 'text-white/90 hover:text-white hover:bg-white/10',
                  isActive && (isScrolled || forceSolid ? 'text-accent-600' : 'text-white')
                )}
              >
                {t(labelKey)}
                <span
                  className={cn(
                    'absolute bottom-0 left-3 right-3 h-0.5 rounded-full transition-all duration-normal',
                    isActive
                      ? 'scale-x-100 opacity-100'
                      : 'scale-x-0 opacity-0 group-hover:scale-x-100 group-hover:opacity-100',
                    isScrolled || forceSolid ? 'bg-accent-600' : 'bg-white'
                  )}
                />
              </LocalizedLink>
            )
          })}
        </div>

        {/* Desktop Actions */}
        <div className="hidden lg:flex items-center gap-2">
          <LanguageSelector variant="desktop" isScrolled={isScrolled || forceSolid} />
        </div>

        {/* Mobile Toggle */}
        <button
          onClick={() => setIsMobileOpen(!isMobileOpen)}
          className={cn(
            'lg:hidden p-2 rounded-md transition-colors',
            isScrolled || forceSolid ? 'text-primary-900 hover:bg-neutral-100' : 'text-white hover:bg-white/10'
          )}
          aria-label={isMobileOpen ? t('aria.closeMenu') : t('aria.openMenu')}
          aria-expanded={isMobileOpen}
        >
          {isMobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </nav>

      {/* Mobile Nav Overlay */}
      <AnimatePresence>
        {isMobileOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="lg:hidden fixed inset-0 top-16 md:top-20 bg-white z-fixed overflow-y-auto"
          >
            <nav className="flex flex-col py-4" aria-label={t('aria.mobileNavigation', 'Mobile navigation')}>
              {navItems.map(({ key, labelKey }) => {
                const isActive = localizedRoutes[key] === currentSlug
                return (
                  <LocalizedLink
                    key={key}
                    to={key}
                    className={cn(
                      'px-6 py-3 text-lg font-medium no-underline transition-colors',
                      isActive
                        ? 'text-accent-600 bg-accent-50 border-l-4 border-accent-600'
                        : 'text-primary-900 hover:bg-neutral-50'
                    )}
                  >
                    {t(labelKey)}
                  </LocalizedLink>
                )
              })}
              <div className="border-t border-neutral-200 mt-4 pt-4">
                <LanguageSelector variant="mobile" />
              </div>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  )
}
